// file_tree — turn the platform's FLAT file listing into a folder tree.
//
// `GET /apps/:slug/files/:env` returns one entry per file, path-only:
//
//   [{ path: "api/groups.ts", bytes: 1840 }, { path: "frontend/src/App.tsx", … }]
//
// …because that is what both deployers store. The explorer needs folders, so the
// grouping happens HERE rather than in the component: it is the only real logic
// in the feature, and platform-ui has no render-test harness — every existing
// test is a pure function (see the house rule in study_platform_ui.md "Seams").
// So the part worth testing lives in a module that can be tested.
//
// WHAT THE PATHS LOOK LIKE, and why we don't prettify them. The listing mixes
// both lanes in the namespace the AGENT writes: backend files bare (`api/`,
// `contract/`, `shared/entities/`, `tests/`, `db/`), frontend files under
// `frontend/`, and e2e specs under `e2e/` (it keeps its own prefix on both
// sides — see platform-service/main.ts's /files handler). That is exactly the
// path an operator would quote in a question, so the tree shows it verbatim.

export type FileNode = { kind: "file"; name: string; path: string; bytes: number };
export type DirNode  = { kind: "dir";  name: string; path: string; children: TreeNode[] };
export type TreeNode = FileNode | DirNode;

export type FileEntry = { path: string; bytes: number };

// Normalise one listed path: posix separators, no leading slash, no empty
// segments. Defensive rather than decorative — slot files were written from a
// Windows workspace until the 2026-09-04 Linux move, and a stray `\` would
// otherwise render as one long filename instead of a folder.
export function normalizePath(raw: string): string {
  return raw.replace(/\\/g, "/").split("/").filter((s) => s.length > 0).join("/");
}

// Folders first, then files, each A→Z case-insensitively. The convention every
// file explorer uses, and the reason it is not just `localeCompare` on the name.
function sortNodes(nodes: TreeNode[]): TreeNode[] {
  nodes.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "dir" ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
  for (const n of nodes) if (n.kind === "dir") sortNodes(n.children);
  return nodes;
}

/**
 * Group a flat listing into a sorted tree.
 *
 * A directory's `path` is its full prefix (`frontend/src`), so a caller can use
 * it directly as a collapse key without reconstructing it.
 *
 * Total: an empty list gives an empty tree, a path that normalises to nothing is
 * skipped, and a duplicate path keeps the LAST entry's size (the listing should
 * not contain one, and silently showing two identical rows would be worse than
 * picking one).
 */
export function buildTree(files: readonly FileEntry[]): TreeNode[] {
  const roots: TreeNode[] = [];
  // prefix → the DirNode holding it, so each folder is created once.
  const dirs = new Map<string, DirNode>();

  for (const entry of files) {
    const clean = normalizePath(entry.path);
    if (clean === "") continue;
    const segments = clean.split("/");
    const fileName = segments.pop()!;

    let siblings = roots;
    let prefix   = "";
    for (const segment of segments) {
      prefix = prefix === "" ? segment : `${prefix}/${segment}`;
      let dir = dirs.get(prefix);
      if (!dir) {
        dir = { kind: "dir", name: segment, path: prefix, children: [] };
        dirs.set(prefix, dir);
        siblings.push(dir);
      }
      siblings = dir.children;
    }

    const path     = clean;
    const bytes    = Number.isFinite(entry.bytes) ? entry.bytes : 0;
    const existing = siblings.findIndex((n) => n.kind === "file" && n.path === path);
    if (existing === -1) siblings.push({ kind: "file", name: fileName, path, bytes });
    else siblings[existing] = { kind: "file", name: fileName, path, bytes };
  }

  return sortNodes(roots);
}

/** Every directory prefix in the tree — what "expand all" needs. */
export function allDirPaths(nodes: readonly TreeNode[]): string[] {
  const out: string[] = [];
  const walk = (ns: readonly TreeNode[]) => {
    for (const n of ns) if (n.kind === "dir") { out.push(n.path); walk(n.children); }
  };
  walk(nodes);
  return out;
}

// Extensions whose bytes are not text. The platform's /source reads every file
// as utf8, so a binary comes back as replacement characters — showing that is
// worse than saying plainly that it cannot be displayed. Deliberately a short
// list of what a generated app actually contains (icons, fonts, the odd image);
// anything unknown is TREATED AS TEXT, because a wrong "binary" verdict hides a
// file the operator asked to see.
const BINARY_EXT = new Set([
  "png", "jpg", "jpeg", "gif", "webp", "avif", "ico", "bmp",
  "woff", "woff2", "ttf", "otf", "eot",
  "pdf", "zip", "gz", "tgz", "br", "wasm", "mp3", "mp4", "webm", "mov",
]);

export function isProbablyBinary(path: string): boolean {
  const base = normalizePath(path).split("/").pop() ?? "";
  const dot  = base.lastIndexOf(".");
  if (dot <= 0) return false;                  // no extension, or a dotfile
  return BINARY_EXT.has(base.slice(dot + 1).toLowerCase());
}

/** "1.8 KB" / "312 B" — sizes in the rail, so a 0-byte file is visibly 0. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
